import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import PixelLogo from '../components/PixelLogo';
import ChatMessage from '../components/ChatMessage';
import ChatInput from '../components/ChatInput';
import OutputPanel from '../components/OutputPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 2000;

const ChatPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const mountedRef = useRef(true);

  const loadProject = useCallback(async () => {
    if (!projectId) return null;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}`, { withCredentials: true });
      if (mountedRef.current) {
        setProject(response.data);
        setLoadError('');
      }
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Unable to load this project.';
      if (mountedRef.current) setLoadError(message);
      return null;
    }
  }, [projectId]);

  useEffect(() => {
    mountedRef.current = true;
    let timer;
    const poll = async () => {
      const latest = await loadProject();
      if (mountedRef.current && latest?.status === 'building') {
        timer = window.setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => {
      mountedRef.current = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [loadProject]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [project?.chat_messages]);

  const sendMessage = async (message) => {
    if (sending || project?.status === 'building') return;
    setSending(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/projects/${projectId}/messages`,
        { message },
        { withCredentials: true }
      );
      setProject(response.data);
      setLoadError('');
      window.setTimeout(loadProject, 500);
    } catch (error) {
      setLoadError(error.response?.data?.detail || 'The message could not be sent. Please retry.');
    } finally {
      setSending(false);
    }
  };

  if (loadError && !project) {
    return (
      <div className="h-screen bg-white grid-bg flex items-center justify-center" data-testid="chat-page-error">
        <div className="text-center space-y-4 max-w-sm px-6">
          <p className="text-[#475569] text-sm">{loadError}</p>
          <button onClick={() => navigate('/dashboard')} className="px-4 py-2 rounded-lg bg-[#2563EB] text-white text-sm font-semibold">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isProcessing = project?.status === 'building' || sending;
  const messages = project?.chat_messages || [];
  const phase = isProcessing ? 'Building in ProductOS' : project?.status === 'waiting_for_input' ? 'Input needed' : '';

  return (
    <div className="h-screen bg-white text-[#0F172A] flex flex-col overflow-hidden" data-testid="chat-page">
      <header className="h-11 flex items-center justify-between px-3 border-b border-[#E2E8F0] bg-white flex-shrink-0 z-30">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate('/dashboard')} aria-label="Back to dashboard" className="p-1.5 rounded-md hover:bg-[#F8FAFC] text-[#475569]">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <PixelLogo size={13} color="#2563EB" />
          <span className="text-xs font-semibold truncate max-w-[45vw]" data-testid="chat-project-name">{project?.name || 'Opening project…'}</span>
          {project?.project_type && <span className="hidden sm:inline text-[10px] font-bold px-2 py-0.5 rounded bg-[#F1F5F9] text-[#64748B]">{project.project_type}</span>}
          {isProcessing && (
            <div className="flex items-center gap-1.5 ml-1 px-2 py-0.5 rounded-md bg-[#EFF6FF] border border-[#2563EB]/20">
              <Loader2 className="w-3 h-3 text-[#2563EB] animate-spin" />
              <span className="text-[10px] text-[#2563EB] font-bold uppercase tracking-wider">{phase}</span>
            </div>
          )}
        </div>
        <button onClick={loadProject} aria-label="Refresh build status" className="p-1.5 rounded-md hover:bg-[#F8FAFC] text-[#64748B]">
          <RefreshCw className="w-4 h-4" />
        </button>
      </header>

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <section className="h-1/2 md:h-full w-full md:w-[38%] md:min-w-[320px] flex flex-col border-b md:border-b-0 md:border-r border-[#E2E8F0] bg-white">
          <div className="flex-1 overflow-y-auto px-4 md:px-5 py-4 space-y-5" data-testid="chat-messages-area" aria-live="polite">
            <AnimatePresence>
              {messages.map((message, index) => (
                <ChatMessage key={message.id || index} role={message.role} content={message.content} />
              ))}
            </AnimatePresence>
            {project?.error_message && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
                <p>{project.error_message}</p>
                <button onClick={() => sendMessage(project.idea)} disabled={sending} className="mt-2 font-semibold underline disabled:opacity-50">Retry build</button>
              </div>
            )}
            {loadError && <p className="text-sm text-red-600" role="alert">{loadError}</p>}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-3 border-t border-[#E2E8F0]" data-testid="chat-input-area">
            <ChatInput onSend={sendMessage} disabled={isProcessing} placeholder={isProcessing ? 'ProductOS is building…' : 'Send a follow-up…'} selectedAgent="auto" onAgentChange={() => {}} />
          </div>
        </section>

        <div className="h-1/2 md:h-full flex-1 min-h-0">
          <OutputPanel researchData={null} prdContent={null} sandboxUrl={project?.sandbox_url || null} isProcessing={isProcessing} currentPhase={isProcessing ? 'code' : null} selectedAgent="auto" />
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
